import React, { useEffect, useMemo, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Col, Container, Navbar, Row } from 'reactstrap';
import classNames from 'classnames';

import useNavigationEntityConverter from '../../../../hooks/navigation/useNavigationEntityConverter';
import globalTranslations from '../../../../i18n/globalTranslations';
import { modelOf } from '../../../../prop-types';
import RouteService from '../../../../services/RouteService';
import CheckoutStore from '../../../../store/CheckoutStore';
import MenuStore from '../../../../store/MenuStore';
import UIStore from '../../../../store/UIStore';
import LinkType from '../../../../types/LinkType';
import Paths from '../../../../types/Paths';
import RequestState from '../../../../types/RequestState';
import AccountLinks from '../../../account/AccountLinks';
import Icon from '../../../common/Icon';
import StickyElement from '../../../common/StickyElement';
import ContentForState from '../../../loader/ContentForState';
import NavigationMenuRow from '../../../navigation/NavigationMenuRow';
import CheckoutHeaderBanner from "../../../ad/CheckoutHeaderBanner";
import CheckoutHeaderMobileNavigation from "../CheckoutHeaderMobileNavigation";
import ClickOutside from "../../../common/ClickOutside";
import MobileNavigation from "../../../header/MobileNavigation";

const CheckoutHeader = ({
  checkoutStore,
  menuStore,
  uiStore,
  menuPosition,
  routeService,
}) => {
  const ref = useRef(null);
  const location = useLocation();
  const { mobileNavigation } = uiStore;

  // Observe changes in the DOM
  useEffect(() => {
      const observer = new MutationObserver(() => {
        if (ref.current && ref.current.offsetHeight !== uiStore.headerHeight) {
          uiStore.setHeaderHeight(ref.current.offsetHeight);
        }
      });

      if (ref.current) {
        observer.observe(ref.current, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true,
        });
      }

      return () => {
        observer.disconnect();
      };
    }, [uiStore]);

  useEffect(() => {
    const maybeLoadMenu = () => {
      if (menuStore && menuStore.checkoutMenuState === RequestState.NONE) {
        menuStore
          .loadCheckoutMenus({
            position: menuPosition,
          })
          .catch((error) => {
            console(error);
          });
      }
    };

    maybeLoadMenu();
  }, [menuPosition, checkoutStore]);

  const getPath = () => {
    const frontPage = routeService.getPath(Paths.FrontPage);
    const pathIsSelf = uiStore.lastPathname === location.pathname;

    if (pathIsSelf) {
      return frontPage;
    } else {
      return uiStore.lastPathname || frontPage;
    }
  };

  const getMenuBarLinks = useMemo(() => {
    let items = [];

    if (menuStore.checkoutMenuState === RequestState.LOADED) {
      items = useNavigationEntityConverter({
        entities: menuStore.checkoutMenus,
        disabled: checkoutStore.checkoutIsLoading,
      });
    }

    const path = getPath();

    items.unshift({
      id: '_top',
      path,
      type: LinkType.INTERNAL_LINK,
      disabled: checkoutStore.checkoutIsLoading,
      image: null,
      label: (
        <>
          <Icon name="chevron-left" />
          <FormattedMessage {...globalTranslations.backToStore} />
        </>
      ),
    });

    return items;
  }, [location]);

  const renderNavigation = () => {
    return uiStore.isMobile
      ? <CheckoutHeaderMobileNavigation />
      : (
        <>
          <ContentForState
            state={menuStore.checkoutMenuState}
            forLoaded={() => {
              return <NavigationMenuRow items={getMenuBarLinks} />;
            }}
          />
          <AccountLinks />
        </>
      );
  }

  return (
    <StickyElement
      ref={ref}
      className={classNames('CheckoutHeader', {
        'CheckoutHeader__mobile-navigation-open': mobileNavigation.isOpen,
      })}
      useFixed={mobileNavigation.isOpen}
    >
      <CheckoutHeaderBanner />
      <Navbar
        className="CheckoutHeader__top d-block"
      >
        <Container className="CheckoutHeader__top-container">
          <Row className="CheckoutHeader__top-inner">
            <Col xs={12} className="CheckoutHeader__top-icons">
              {renderNavigation()}
            </Col>
          </Row>
        </Container>
      </Navbar>
      {uiStore.isMobile && mobileNavigation.isOpen &&
        <div style={{
          position: 'fixed',
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(5px)',
        }}>
        <ClickOutside
          className="MobileNavigation__container"
          style={{ maxHeight: mobileNavigation.mobileMenuHeight }}
          onClickOutside={mobileNavigation.toggle}
          excludedSelector=".MobileNavigationButton, .modal"
        >
          <MobileNavigation
            shouldHideProductContent={true}
            navigation={mobileNavigation}
            menuPosition={menuPosition}
          />
        </ClickOutside>
        </div>
      }
    </StickyElement>
  );
};

CheckoutHeader.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  menuStore: modelOf(MenuStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  menuPosition: PropTypes.string,
};

export default inject(
  'checkoutStore',
  'menuStore',
  'uiStore',
  'routeService'
)(observer(CheckoutHeader));
